import { firstValueFrom } from 'rxjs';
/** ***********
*
* <quick-link.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { Component, ElementRef, QueryList, ViewChildren, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Validators, FormBuilder, FormGroup, FormControl, ValidationErrors, AbstractControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable, ReplaySubject, Subscription, iif, of, timer } from 'rxjs';
import { map, switchMap, tap, distinctUntilKeyChanged, filter } from 'rxjs/operators';
import { selectTrackersArray$ } from '../../store/selectors/tracker.selectors';
import { AssetService, CreateAsset, TrackerLinkError } from 'src/app/services/asset.service';
import { Asset } from 'src/app/models/asset';
import { Tracker } from 'src/app/models/tracker';
import { associateTrackerToNewAsset, dissociateTrackerFromAsset } from 'src/app/store/actions/asset.actions';
import { selectAssetByTrackerId } from 'src/app/store/selectors/asset.selectors';
import { SignalSourceProviderValues } from 'src/app/models/signal-source';
import { Family, ParentFamily } from 'src/app/models/family';
import { MatStep } from '@angular/material/stepper';
import { AssetEffects } from 'src/app/store/effects/asset.effects';

@Component({
    selector: 'app-quick-link',
    templateUrl: './quick-link.component.html',
    styleUrls: ['./quick-link.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class QuickLinkComponent implements OnInit, OnDestroy {

    @ViewChild('deviceSn') deviceSnInput: ElementRef;
    @ViewChild('configureAssetStep') configureAssetStep?: MatStep;
    @ViewChild('reviewStep') reviewStep: MatStep;

    public checkTrackerValidity = false;
    public currentStepIndex= 0;
    public submiting= false;

    public trackerSelectionForm: FormGroup<{
        tracker: FormControl<Tracker['serial']>;
    }> = this._fb.group({
        tracker: this._fb.control(null, [Validators.required], [
            (control: AbstractControl): Observable<ValidationErrors | null> => {
                // Debounce 500ms time before checking the validity
                this.checkTrackerValidity = true;
                this._cd.markForCheck();
                return timer(500).pipe(
                    switchMap(() => this._assetService.getLinkStatus(control.value)),
                    map((v) => {
                        if ((v as TrackerLinkError).error?.message) {
                            this.selectedTracker$.next(null);
                            return { linkError: (v as TrackerLinkError).error.message };
                        }
                        this.selectedTracker$.next(v as Tracker);
                        return null;
                    }),
                    tap(() => {
                        this.checkTrackerValidity = false;
                        this._cd.markForCheck();
                    }),
                );
            }
        ]),
    });

    public dissoManagementForm: FormGroup = this._fb.group({});
    public assoManagementForm: FormGroup<{
        parentFamily: FormControl<ParentFamily['id']>;
        family: FormControl<Family>;
        msn: FormControl<string>;
        side: FormControl<'G'|'D'>; // G = left (gauche), D = right (droit)
    }> = this._fb.group({
        parentFamily: this._fb.control(null, [Validators.required]),
        family: this._fb.control(null, [Validators.required]),
        msn: this._fb.control(null, [Validators.required]),
        side: this._fb.control(null, [Validators.required]),
    });

    public trackers$ = this.store.select(selectTrackersArray$).pipe(
        map(trackers => trackers.filter(
            ({ provider }) => provider === SignalSourceProviderValues.sensolus
        )),
    );
    public selectedTracker$ = new ReplaySubject<Tracker>(1);
    public assetOfSelectedTracker$ = this.selectedTracker$.pipe(
        filter(tracker => !!tracker?.id),
        distinctUntilKeyChanged('id'),
        switchMap((tracker) => iif(
            () => !!tracker.assetId,
            this.store.select(selectAssetByTrackerId(tracker.id)),
            of(null),
        )),
    );


    private _subscription: Subscription = new Subscription();
    public trackerList: Tracker[] = [];
    // Autocomplete Trigger Collection
    @ViewChildren(MatAutocompleteTrigger) triggerCollection: QueryList<MatAutocompleteTrigger>;

    constructor(
        private readonly store: Store,
        private readonly _fb: FormBuilder,
        private readonly _cd: ChangeDetectorRef,
        private readonly _assetService: AssetService,
        private _assetEffects: AssetEffects,
    ) { }

    public ngOnInit(): void {
        this._subscription.add(
            this.selectedTracker$.pipe(
                // Ensure the key id exists
                map(tracker => tracker ?? { id: null }),
                distinctUntilKeyChanged('id'),
            ).subscribe(
                () => {
                    // Reset the steps when the tracker is changed
                    this.configureAssetStep?.reset();
                    this.reviewStep.reset();
                }
            )
        );
    }

    public async complete() {
        this.submiting = true;
        const tracker = await firstValueFrom(this.selectedTracker$);
        const asset = await firstValueFrom(this.assetOfSelectedTracker$);
        if (tracker.associated) {
            this.dissociateDevice(tracker, asset);
        } else {
            this.associateDevice(tracker);
        }
    }

    /**
     * Link Asset Process
     */
    private associateDevice(tracker: Tracker) {
        const { value } = this.assoManagementForm;
        const sectionWay4CA = 'SA S PYLONS'; // Compatible Section forced for now.
        const assetPayload: CreateAsset = {
            name: `${value.msn}-${value.side}`,
            familyId: value.family.id,
            trackerId: tracker.id,
            sn: tracker.serial,
            section: sectionWay4CA,
            msn: value.msn,
            side: value.side,
        };
        this._subscription.add(
            this._assetEffects.effectSubject.subscribe((result) => {
              if (result === 'success') {
                this.resetFilters();
              }
              this.submiting = false;
            })
          );
        this.store.dispatch(associateTrackerToNewAsset({ asset: assetPayload }));
    }

    /**
     * Unlink Asset process
     */
    private dissociateDevice(tracker: Tracker, asset: Asset) {
        this._subscription.add(
            this._assetEffects.effectSubject.subscribe((result) => {
              if (result === 'success') {
                this.resetFilters();
              }
              this.submiting = false;
            })
          );
        this.store.dispatch(dissociateTrackerFromAsset({ asset, tracker }));
    }

    /**
     * Reset All filters
     */

    public resetFilters() {
        this.resetTrackerSelectionForm();
        this.configureAssetStep?.reset();
        this.reviewStep.reset();
        this.currentStepIndex = 0;
    }

    private resetTrackerSelectionForm() {
        this.trackerSelectionForm.reset(); // Reset the form    
        // Clear errors and mark controls as untouched in a more concise way
        const trackerControl = this.trackerSelectionForm.get('tracker');
        if (trackerControl) {
            trackerControl.setErrors(null); // Clear any errors
            trackerControl.markAsUntouched(); // Mark as untouched
        }
        this.selectedTracker$.next(null);
    }


    ngOnDestroy(): void {
        this._subscription.unsubscribe();
    }
}
